import {signal} from '@angular/core';
import {CycleListType} from '@core/api/data-access';
import {CycleQueryBuilder, QueryCyclesData} from '@cycles/data-access';
import {DateRangeType, QueryCycleSortMarker} from '@global-data';
import {Cached} from '@utils';
import {GlobalSearchCategory, GlobalSearchFilterType, IFilterUpdateResult} from '../types/global-search.types';
import {FilterDefinition, GlobalSearchAbstractCategory, ICategorySortOptions} from './global-search-category-config.abstract';

export class CycleCategory extends GlobalSearchAbstractCategory<CycleQueryBuilder, QueryCycleSortMarker> {
	public category: GlobalSearchCategory = GlobalSearchCategory.CYCLE;
	public static CycleHelperData = QueryCyclesData.getQueryCycleData();
	private cycleHelperData = CycleCategory.CycleHelperData;
	private lastFilterValue: CycleListType[] = [CycleListType.Waiting, CycleListType.Finished];

	/* ---------------------- Sorting ---------------------- */
	public static readonly CycleSortOptions = this.CycleHelperData.Sorting.markers;
	public readonly sortOptions: ICategorySortOptions<QueryCycleSortMarker>[] = Object.values(this.cycleHelperData.Sorting.markers);

	/* ---------------------- Filters ---------------------- */
	private readonly CycleListsSelect = new FilterDefinition<CycleQueryBuilder, CycleListType>({
		type: GlobalSearchFilterType.Status,
		controlType: 'select',
		labelKey: 'filters.cycle.status',
		placeholder: 'filters.allStatuses',
		icon: 'fa-duotone fa-tags',
		defaultValue: [CycleListType.All],
		options: signal(
			Object.values(this.cycleHelperData.CycleLists.byCategory).map(category => {
				const groupLabel = category.label;
				const groupOptions = Object.entries(category.types).map(([itemKey, categoryList]) => ({
					label: categoryList.label,
					value: itemKey as CycleListType,
				}));
				return {
					label: groupLabel,
					value: category.label as CycleListType,
					items: groupOptions,
				};
			}),
		),
		refetchOn: 'SelectPanelClosed',
		onValueChanged: (value: CycleListType[]): IFilterUpdateResult<CycleListType[]> => {
			// Check if All was deselected (previous value had All, current value doesn't)
			const prevValueHadAll = this.lastFilterValue?.includes(CycleListType.All);
			const currentValueHasAll = value?.includes(CycleListType.All);

			if (prevValueHadAll && this.lastFilterValue.length !== value?.length) {
				// If 'All' was previously selected and the current value length is different, we need unset the 'All' selection
				value = value?.filter(item => item !== CycleListType.All) || [];
			}

			// If no value is selected, return to default values
			// If All was deselected, return to default values
			if (!value?.length || (prevValueHadAll && !currentValueHasAll)) {
				return {
					normalizedValue: this.CycleListsSelect.defaultValue as CycleListType[],
					requiresSearch: true,
				};
			}

			// handle 'All' selection
			if (value?.includes(CycleListType.All)) {
				value = Object.values(CycleListType) as CycleListType[];
			}

			// Update last value for future reference
			this.lastFilterValue = [...(value || [])];

			return {
				normalizedValue: value?.length ? value : undefined,
				requiresSearch: false,
			};
		},
		applyFilter: (builder: CycleQueryBuilder, value: CycleListType[]): void => {
			// if 'All' is selected we need to set all lists
			if (value?.includes(CycleListType.All)) {
				// remove the lists from the builder
				builder.setLists([]);
				return;
			}

			// If no value is selected, use default value
			if (!value?.length) {
				builder.setLists(this.CycleListsSelect.defaultValue as string[]);
				return;
			}

			// Otherwise set the selected lists
			builder.setLists(value as string[]);
		},
		controlOptions: {
			styleClass: 'filter-item dense-min',
			SELECT: {
				multi: true,
				group: true,
				filter: false,
				showClear: false,
				filterBy: 'label',
				optionLabel: 'label',
				optionValue: 'value',
			},
		},
	});
	private readonly CreateDateRange = new FilterDefinition<CycleQueryBuilder, [Date | null, Date | null]>({
		type: GlobalSearchFilterType.CreateDateRange,
		labelKey: this.cycleHelperData.DateRange.types[DateRangeType.CreateDate].label,
		placeholder: 'filters.createDateRangePlaceholder',
		icon: this.cycleHelperData.DateRange.types[DateRangeType.CreateDate].icon,
		controlType: 'date',
		onValueChanged: this.validateDateRange,
		applyFilter: (builder: CycleQueryBuilder, value: [Date | null, Date | null]): void => {
			if (value?.[0] || value?.[1]) {
				builder.setDateRange(value[0] ?? undefined, value[1] ?? undefined, DateRangeType.CreateDate);
			}
		},
		controlOptions: {
			styleClass: 'filter-item dense-min',
			DATE: {
				selectionMode: 'range',
				appendTo: 'body',
				readonlyInput: true,
				dateFormat: 'dd/mm/yy',
				showClear: true,
				maxDate: new Date(),
			},
		},
	});
	private readonly UpdateDateRange = new FilterDefinition<CycleQueryBuilder, [Date | null, Date | null]>({
		type: GlobalSearchFilterType.UpdateDateRange,
		labelKey: this.cycleHelperData.DateRange.types[DateRangeType.LastUpdate].label,
		placeholder: 'filters.updateDateRangePlaceholder',
		icon: this.cycleHelperData.DateRange.types[DateRangeType.LastUpdate].icon,
		controlType: 'date',
		onValueChanged: this.validateDateRange,
		applyFilter: (builder: CycleQueryBuilder, value: [Date | null, Date | null]): void => {
			if (value?.[0] || value?.[1]) {
				builder.setDateRange(value[0] ?? undefined, value[1] ?? undefined, DateRangeType.LastUpdate);
			}
		},
		controlOptions: {
			styleClass: 'filter-item dense-min',
			DATE: {
				selectionMode: 'range',
				appendTo: 'body',
				readonlyInput: true,
				dateFormat: 'dd/mm/yy',
				showClear: true,
				maxDate: new Date(),
			},
		},
	});

	@Cached()
	getFilters(): FilterDefinition<CycleQueryBuilder, any>[] {
		return [this.CycleListsSelect, this.CreateDateRange, this.UpdateDateRange];
	}

	private validateDateRange(value: [Date | null, Date | null] | undefined): IFilterUpdateResult<[Date | null, Date | null] | undefined> {
		return {
			normalizedValue: !value ? undefined : [value[0] || undefined, value[1] || undefined],
			requiresSearch: !value || (value[0] !== null && value[1] !== null),
		};
	}
}
